<template>
  <div>
    <h2 v-if="!compact" class="mc-h2">What happened?</h2>

    <div class="mc-field">
      <label class="mc-lbl" for="mc-when"
        >Date and time <span class="mc-req">*</span></label
      >
      <input
        id="mc-when"
        type="datetime-local"
        :value="value.when"
        @input="update('when', $event.target.value)"
      />
      <p class="mc-hint">
        Defaults to now — change it if the incident was earlier.
      </p>
    </div>

    <div class="mc-field">
      <p class="mc-lbl">Where did it happen? <span class="mc-req">*</span></p>
      <p class="mc-hint mc-mb9">
        Are you at the scene of the accident right now?
      </p>
      <segmented-control
        class="mc-mb12"
        :options="[
          { label: 'Yes, I’m at the scene', value: true },
          { label: 'No, I’ve left', value: false },
        ]"
        :value="value.atScene"
        @input="setAtScene"
      />
      <button
        v-if="value.atScene !== null"
        type="button"
        class="mc-btn mc-btn--sec mc-wide"
        @click="openLocationSheet"
      >
        📍 Set the location
      </button>
      <div v-if="value.incidentLocation.locationName" class="mc-loc-card">
        <span>📍</span>
        <div class="mc-loc-text">{{ value.incidentLocation.locationName }}</div>
        <button type="button" class="mc-linkbtn" @click="openLocationSheet">
          Change
        </button>
      </div>
      <p v-if="value.atScene === null" class="mc-hint">
        Choose one so we can pin the accident location.
      </p>
    </div>

    <div class="mc-field">
      <label class="mc-lbl" for="mc-story"
        >Tell us what happened, in your own words
        <span class="mc-req">*</span></label
      >
      <textarea
        id="mc-story"
        placeholder="Start from just before the accident and describe what happened…"
        :value="value.story"
        @input="update('story', $event.target.value)"
      />
      <p class="mc-hint">
        {{ value.story.trim().length }} characters — a short paragraph is
        plenty.
      </p>
    </div>

    <h3 class="mc-h3">Road surface</h3>
    <chip-group
      :options="SURFACES"
      :value="value.surface"
      @input="update('surface', $event)"
    />
    <h3 class="mc-h3">Weather</h3>
    <chip-group
      :options="WEATHER"
      :value="value.weather"
      @input="update('weather', $event)"
    />
    <h3 class="mc-h3">Visibility</h3>
    <chip-group
      :options="VIS"
      :value="value.vis"
      @input="update('vis', $event)"
    />
    <h3 class="mc-h3">Lighting</h3>
    <chip-group
      :options="LIGHT"
      :value="value.light"
      @input="update('light', $event)"
    />

    <h3 class="mc-h3">Did police attend?</h3>
    <segmented-control
      class="mc-mb14"
      :options="[
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ]"
      :value="value.police"
      @input="update('police', $event)"
    />
    <div v-if="value.police === true" class="mc-row2">
      <div class="mc-field">
        <label class="mc-lbl" for="mc-station">Police station</label>
        <input
          id="mc-station"
          type="text"
          placeholder="e.g. Kasarani"
          :value="value.station"
          @input="update('station', $event.target.value)"
        />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="mc-ob">OB / constable number</label>
        <input
          id="mc-ob"
          type="text"
          placeholder="OB/00/00/0000"
          :value="value.ob"
          @input="update('ob', $event.target.value)"
        />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="mc-police-phone">Police telephone number (optional)</label>
        <input id="mc-police-phone" type="tel" placeholder="e.g. 0712345678" :value="value.policeTel" @input="update('policeTel', $event.target.value)" />
      </div>
      <div class="mc-field">
        <label class="mc-lbl" for="mc-traffic-offence">Traffic offence number (optional)</label>
        <input id="mc-traffic-offence" type="text" placeholder="Traffic offence number" :value="value.trafficOffenceNumber" @input="update('trafficOffenceNumber', $event.target.value)" />
      </div>
    </div>

    <h3 class="mc-h3">Who was driving?</h3>
    <segmented-control class="mc-mb12" :options="[{ label: 'Me', value: true }, { label: 'Someone else', value: false }]" :value="value.driverWasMe" @input="setDriver" />
    <div v-if="value.driverWasMe === true" class="mc-field">
      <label class="mc-lbl" for="mc-driver-email">Your email address</label>
      <input id="mc-driver-email" type="email" placeholder="e.g. driver@example.com" :value="value.driverEmail" @input="update('driverEmail', $event.target.value)" />
    </div>

    <div v-if="value.driverWasMe === false" class="mc-field">
      <label class="mc-lbl" for="mc-otherdriver">Their name</label>
      <input id="mc-otherdriver" type="text" placeholder="Full name" :value="value.otherDriverName" @input="update('otherDriverName', $event.target.value)" />
    </div>

    <div v-if="value.driverWasMe === false" class="mc-field">
      <label class="mc-lbl" for="mc-otherdriver-email">Their email address</label>
      <input id="mc-otherdriver-email" type="email" placeholder="e.g. driver@example.com" :value="value.otherDriverEmail" @input="update('otherDriverEmail', $event.target.value)" />
    </div>

    <div v-if="value.driverWasMe === false" class="mc-field">
      <label class="mc-lbl" for="mc-otherdriver-phone">Their phone number</label>
      <input id="mc-otherdriver-phone" type="tel" placeholder="e.g. 0712345678" :value="value.otherDriverPhone" @input="update('otherDriverPhone', $event.target.value)" />
    </div>

    <div v-if="value.driverWasMe === false" class="mc-field">
      <label class="mc-lbl" for="mc-otherdriver-licence">Their driving licence number</label>
      <input id="mc-otherdriver-licence" type="text" placeholder="e.g. DL1234567" :value="value.otherDriverLicenceNo" @input="update('otherDriverLicenceNo', $event.target.value)" />
    </div>

    <p v-if="value.driverWasMe === true && value.insured && value.insured.name" class="mc-mb12">
      Driver: {{ value.insured.name }}{{ value.insured.tel ? ` · ${value.insured.tel}` : '' }}
    </p>
    <template v-if="value.driverWasMe != null">
      <h3 class="mc-h3">{{ value.driverWasMe ? 'Are you to blame for the accident?' : 'Was the driver to blame for the accident?' }}</h3>
      <segmented-control :options="[{ label: 'Yes', value: true }, { label: 'No', value: false }]" :value="value.driverToBlame" @input="update('driverToBlame', $event)" />
    </template>

    <bottom-sheet :open="sheetOpen" @close="closeLocationSheet">
      <location-picker
        v-if="sheetOpen"
        :value="value.incidentLocation"
        @select="selectLocation"
        @cancel="closeLocationSheet"
        @error="(msg) => $emit('toast', msg)"
      />
    </bottom-sheet>
  </div>
</template>

<script>
import SegmentedControl from '../shared/SegmentedControl.vue'
import ChipGroup from '../shared/ChipGroup.vue'
import BottomSheet from '../shell/BottomSheet.vue'
import LocationPicker from '../shared/LocationPicker.vue'

const SURFACES = ['Dry', 'Wet', 'Loose gravel', 'Potholed', 'Muddy']
const WEATHER = ['Clear', 'Light rain', 'Heavy rain', 'Fog', 'Dusty']
const VIS = ['Good', 'Moderate', 'Poor']
const LIGHT = ['Daylight', 'Dusk or dawn', 'Street lights', 'No lights']

export default {
  name: 'WhatHappenedStep',
  components: { SegmentedControl, ChipGroup, BottomSheet, LocationPicker },
  props: {
    // { when, incidentLocation: {latitude,longitude,locationName}, atScene,
    //   story, surface, weather, vis, light, police, station, ob,
    //   policeTel, trafficOffenceNumber, driverWasMe, driverEmail, otherDriverName,
    //   otherDriverEmail, otherDriverPhone, otherDriverLicenceNo,
    //   driverToBlame, insured }
    value: { type: Object, required: true },
    // rendered inside a review card, which already supplies the heading
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      SURFACES,
      WEATHER,
      VIS,
      LIGHT,
      sheetOpen: false,
    }
  },
  methods: {
    update(key, val) {
      this.$emit('input', { ...this.value, [key]: val })
    },
    setDriver(driverWasMe) {
      this.$emit('input', {
        ...this.value,
        driverWasMe,
        driverToBlame:
          driverWasMe === this.value.driverWasMe
            ? this.value.driverToBlame
            : null,
      })
    },
    setAtScene(v) {
      this.update('atScene', v)
      this.openLocationSheet()
    },
    openLocationSheet() {
      this.sheetOpen = true
    },
    closeLocationSheet() {
      this.sheetOpen = false
    },
    selectLocation(location) {
      this.update('incidentLocation', location)
      this.closeLocationSheet()
      this.$emit('toast', 'Location set')
    },
  },
}
</script>

<style scoped>
.mc-h2 {
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #14142b;
}

.mc-h3 {
  font-size: 13px;
  font-weight: 600;
  margin: 22px 0 10px;
  color: #3d4450;
}

.mc-lede {
  font-size: 14.5px;
  color: #6e7191;
  margin: 0 0 20px;
}

.mc-field {
  margin: 0 0 16px;
}

@media (min-width: 900px) {
  .mc-row2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .mc-row2 .mc-field {
    margin-bottom: 0;
  }
}

.mc-lbl {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #3d4450;
  margin-bottom: 7px;
}

/* visually hidden but still readable by screen readers — used where a
   placeholder is doing the visible labelling job */
.mc-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.mc-req {
  color: #eb5757;
}

input,
textarea {
  width: 100%;
  font-family: inherit;
  font-size: 16px;
  color: #333333;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 13px 14px;
  appearance: none;
}

textarea {
  min-height: 104px;
  resize: vertical;
  line-height: 1.55;
}

input:focus,
textarea:focus {
  outline: 0;
  border-color: #0066cc;
  box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.13);
}

.mc-hint {
  font-size: 12.5px;
  color: #6e7191;
  margin: 6px 0 0;
}

.mc-mb9 {
  margin: 0 0 9px;
}

.mc-mb12 {
  margin-bottom: 12px;
}

.mc-mb14 {
  margin-bottom: 14px;
}

.mc-mt8 {
  margin-top: 8px;
}

.mc-mt16 {
  margin-top: 16px;
}

.mc-wide {
  width: 100%;
}

.mc-loc-card {
  display: flex;
  gap: 11px;
  align-items: center;
  background: #eef3ff;
  border: 1px solid #bdd8f5;
  border-radius: 14px;
  padding: 15px;
  margin-top: 11px;
}

.mc-loc-text {
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  color: #002247;
  font-weight: 600;
}

.mc-linkbtn {
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #0052b3;
  cursor: pointer;
  padding: 4px 0;
}
</style>
